import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import type { WorkflowItem } from "@/api/models"
import type { Project } from "@/services/project-service"

import {
  ChevronDown,
  ChevronRight,
  FolderKanban,
  History,
  LayoutGrid,
  Loader2,
  Play,
  Sparkles,
  TestTube2,
  User,
  Users,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  WORKFLOW_EDITOR_TOOLBAR_ACTIONS_CLASS,
  WORKFLOW_EDITOR_TOOLBAR_CLASS,
  WORKFLOW_EDITOR_TOOLBAR_CONTROLS_CLASS,
} from "../utils/workflow-editor-toolbar-utils"

type WorkflowVisibility = WorkflowItem["visibility"]

type WorkflowEditorToolbarProps = {
  workflowId?: string
  workflowTitle: string
  onTitleChange: (title: string) => void
  workflowVisibility: WorkflowVisibility
  onVisibilityChange: (visibility: WorkflowVisibility) => void
  defaultProjectId: string | undefined
  onProjectChange: (projectId: string | undefined) => void
  projects: Project[]
  canEdit: boolean
  isWorkflowAdmin: boolean
  isAutoSaving: boolean
  isSaving: boolean
  isAiChatOpen: boolean
  showTestPanel: boolean
  onToggleAiChat: () => void
  onSave: () => void
  onTestRun: () => void
  onRunInExplorer: () => void
  onOpenRunHistory: () => void
  pausedRunCount?: number
}

export function WorkflowEditorToolbar({
  workflowId,
  workflowTitle,
  onTitleChange,
  workflowVisibility,
  onVisibilityChange,
  defaultProjectId,
  onProjectChange,
  projects,
  canEdit,
  isWorkflowAdmin,
  isAutoSaving,
  isSaving,
  isAiChatOpen,
  showTestPanel,
  onToggleAiChat,
  onSave,
  onTestRun,
  onRunInExplorer,
  onOpenRunHistory,
  pausedRunCount = 0,
}: WorkflowEditorToolbarProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const readOnlyVisibilityIcon =
    workflowVisibility === "personal" ? (
      <User className="h-3.5 w-3.5" />
    ) : (
      <Users className="h-3.5 w-3.5" />
    )
  let readOnlyVisibilityLabel = t("editor.personal")

  if (workflowVisibility === "company") {
    readOnlyVisibilityLabel = t("editor.company")
  } else if (workflowVisibility === "shared") {
    readOnlyVisibilityLabel = t("tabs.shared", "Shared")
  }

  return (
    <div className={WORKFLOW_EDITOR_TOOLBAR_CLASS} data-testid="workflow-editor-toolbar">
      <div className={WORKFLOW_EDITOR_TOOLBAR_CONTROLS_CLASS}>
        <nav className="flex shrink-0 items-center gap-1 text-sm">
          <Link
            className="text-muted-foreground hover:text-foreground transition-colors"
            to="/flows"
          >
            {t("editor.breadcrumb")}
          </Link>
          <ChevronRight className="text-muted-foreground h-4 w-4" />
        </nav>
        <Input
          className="h-8 min-w-[150px] flex-1 sm:w-[200px] sm:flex-none"
          disabled={!canEdit}
          value={workflowTitle}
          onChange={(e) => onTitleChange(e.target.value)}
        />
        {isWorkflowAdmin && canEdit ? (
          <Select
            disabled={!canEdit}
            value={workflowVisibility}
            onValueChange={(value: "personal" | "company") => onVisibilityChange(value)}
          >
            <SelectTrigger className="h-8 min-w-[130px] sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">
                <span className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5" />
                  {t("editor.personal")}
                </span>
              </SelectItem>
              <SelectItem value="company">
                <span className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" />
                  {t("editor.company")}
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="bg-muted/50 text-muted-foreground flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs">
            {readOnlyVisibilityIcon}
            <span>{readOnlyVisibilityLabel}</span>
          </span>
        )}
        <Select
          disabled={!canEdit}
          value={defaultProjectId ?? "none"}
          onValueChange={(value) => onProjectChange(value === "none" ? undefined : value)}
        >
          <SelectTrigger className="h-8 min-w-[160px] flex-1 sm:w-[180px] sm:flex-none">
            <FolderKanban className="text-muted-foreground mr-1.5 h-3.5 w-3.5" />
            <SelectValue placeholder={t("editor.noDefaultProject")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">{t("editor.noDefaultProject")}</span>
            </SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className={WORKFLOW_EDITOR_TOOLBAR_ACTIONS_CLASS}
        data-testid="workflow-editor-toolbar-actions"
      >
        {isAutoSaving && (
          <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <Loader2 className="size-3 animate-spin" />
            {t("editor.autoSaving")}
          </span>
        )}
        {canEdit && (
          <Button
            className={isAiChatOpen ? "bg-muted" : ""}
            size="sm"
            variant="outline"
            onClick={onToggleAiChat}
          >
            <Sparkles className="mr-1 size-3" />
            AI Builder
          </Button>
        )}
        <Button asChild size="sm" variant="ghost">
          <Link to="/flows#flows">
            <LayoutGrid className="mr-1 size-3" />
            {t("viewFlows")}
          </Link>
        </Button>
        {workflowId && (
          <Button className="relative" size="sm" variant="outline" onClick={onOpenRunHistory}>
            <History className="mr-1 size-3" />
            {t("runs.title", "Run history")}
            {pausedRunCount > 0 && (
              <span className="border-foreground bg-destructive text-destructive-foreground ml-1 rounded-full border px-1.5 py-0.5 text-[10px] leading-none">
                {pausedRunCount}
              </span>
            )}
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className={showTestPanel ? "bg-muted" : ""} size="sm" variant="outline">
              <Play className="mr-1 size-3" />
              {t("editor.run")}
              <ChevronDown className="ml-1 size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onTestRun}>
              <TestTube2 className="mr-2 size-4" />
              {t("editor.testRun")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onRunInExplorer}>
              <Play className="mr-2 size-4" />
              {t("editor.runInExplorer")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button disabled={isSaving || !canEdit} size="sm" onClick={onSave}>
          {isSaving ? t("editor.saving") : t("editor.saveWorkflow")}
        </Button>
      </div>
    </div>
  )
}
